'use client';

/**
 * FlowTime — таймер производительности.
 *
 * Собирает вкладки в одно рабочее место и держит то, что общее для всех:
 * заголовок вкладки браузера, уведомления, горячие клавиши и реакцию на
 * окончание интервала.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { BarChart3, ListChecks, Settings, Timer } from 'lucide-react';
import { useFlowTime, isBreak } from '@/components/datahunter/use-flowtime';
import { FlowTimer } from '@/components/datahunter/flowtime-timer';
import { FlowSidebar } from '@/components/datahunter/flowtime-sidebar';
import { FlowLog } from '@/components/datahunter/flowtime-log';
import { FlowReports } from '@/components/datahunter/flowtime-reports';
import { FlowSettingsPanel } from '@/components/datahunter/flowtime-settings';
import { fmtClock, fmtDur } from '@/lib/flowtime-format';
import { unlockAudio } from '@/lib/flowtime-audio';

type Tab = 'timer' | 'log' | 'reports' | 'settings';

const TABS: { id: Tab; icon: typeof Timer }[] = [
  { id: 'timer', icon: Timer },
  { id: 'log', icon: ListChecks },
  { id: 'reports', icon: BarChart3 },
  { id: 'settings', icon: Settings },
];

interface Toast {
  id: number;
  message: string;
  tone: 'ok' | 'error' | 'plain';
}

export function FlowTime({ title }: { title: string }) {
  const t = useTranslations('dh_flowtime_ui');
  const locale = useLocale();
  const flow = useFlowTime();
  const [tab, setTab] = useState<Tab>('timer');
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);

  const notify = useCallback((message: string, tone: 'ok' | 'error' | 'plain' = 'plain') => {
    const id = ++toastId.current;
    setToasts((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 3600);
  }, []);

  // ---------------------------------------------------------------------
  // Заголовок вкладки
  // ---------------------------------------------------------------------

  useEffect(() => {
    if (!flow.ready) return;
    const base = `${title} — DataHunter`;

    if (!flow.settings.titleCountdown || !flow.active) {
      document.title = base;
      return;
    }

    const shown = flow.mode === 'free' ? flow.elapsedMs : flow.remainingMs;
    document.title = `${fmtClock(shown / 1000)} · ${t(`mode_${flow.mode}`)} — FlowTime`;

    return () => {
      document.title = base;
    };
  }, [
    flow.ready,
    flow.settings.titleCountdown,
    flow.active,
    flow.mode,
    flow.elapsedMs,
    flow.remainingMs,
    title,
    t,
  ]);

  // ---------------------------------------------------------------------
  // Окончание интервала
  // ---------------------------------------------------------------------

  useEffect(() => {
    flow.onCompletion(({ from, natural, offline, workedMs, entry }) => {
      const minutes = Math.round(workedMs / 60_000);
      const canNotify =
        flow.settings.notify &&
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted';

      if (from === 'focus' && natural) {
        const task = entry?.taskId ? flow.taskById(entry.taskId) : null;

        if (canNotify) {
          new Notification(t('notify_focus_done'), {
            body: task
              ? t('notify_focus_body_task', { task: task.title, minutes })
              : t('notify_focus_body', { minutes }),
            tag: 'flowtime',
            silent: true,
          });
        }

        notify(
          offline ? t('msg_pomodoro_offline', { minutes }) : t('msg_pomodoro', { minutes }),
          'ok',
        );

        // Дневная цель отмечается один раз, в момент пересечения: постоянное
        // напоминание после её достижения быстро надоедает.
        const goal = flow.settings.goalHours * 3600;
        if (goal > 0) {
          const before = flow.data.entries
            .filter((e) => e.mode === 'focus' && e.id !== entry?.id)
            .reduce((s, e) => s + (e.end - e.start) / 1000, 0);
          const after = before + workedMs / 1000;
          if (before < goal && after >= goal) {
            setTimeout(() => {
              notify(t('msg_goal', { goal: fmtDur(goal, locale) }), 'ok');
            }, 900);
          }
        }
      } else if (from === 'focus' && !natural && minutes >= 1) {
        notify(t('msg_stopped', { minutes }));
      } else if (isBreak(from) && natural) {
        if (canNotify) {
          new Notification(t('msg_break_over'), {
            body: t('notify_break_body'),
            tag: 'flowtime',
            silent: true,
          });
        }
        notify(t('msg_break_over'));
      }
    });
  }, [flow, notify, t, locale]);

  useEffect(() => {
    if (flow.recovered) notify(t('msg_recovered'), 'error');
  }, [flow.recovered, notify, t]);

  // ---------------------------------------------------------------------
  // Горячие клавиши
  // ---------------------------------------------------------------------

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      const typing =
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement ||
        (el instanceof HTMLElement && el.isContentEditable);
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.code === 'Space') {
        e.preventDefault();
        if (flow.running && flow.settings.strict) {
          notify(t('msg_strict'));
          return;
        }
        flow.toggle();
        return;
      }

      const key = e.key.toLowerCase();
      if (key === 'r' && flow.active) {
        e.preventDefault();
        flow.reset(flow.elapsedMs < 60_000);
      } else if (key === 's') {
        e.preventDefault();
        flow.skip();
      } else if (key === 'i' && flow.active) {
        e.preventDefault();
        flow.addInterruption();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [flow, notify, t]);

  // Web Audio включается только после действия человека: до этого браузер
  // держит звук выключенным, и сигнал окончания не прозвучал бы.
  useEffect(() => {
    const unlock = () => {
      unlockAudio(flow.settings.volume);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [flow.settings.volume]);

  // Случайно закрытая вкладка во время работы теряет незаписанную сессию.
  useEffect(() => {
    if (!flow.running || flow.mode !== 'focus') return;
    const onLeave = (e: BeforeUnloadEvent) => {
      // Достаточно preventDefault: браузеры показывают своё сообщение,
      // а returnValue объявлен устаревшим.
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onLeave);
    return () => {
      window.removeEventListener('beforeunload', onLeave);
    };
  }, [flow.running, flow.mode]);

  // Разметка ждёт чтения localStorage: иначе сервер отдал бы «25:00»,
  // а браузер тут же заменил бы это временем прерванной сессии.
  if (!flow.ready) {
    return (
      <div className="max-w-[1280px] mx-auto px-6 py-10">
        <div className="h-[520px] rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] animate-pulse" />
      </div>
    );
  }

  const todayTotal = flow.data.entries
    .filter(
      (e) => e.mode === 'focus' && new Date(e.start).toDateString() === new Date().toDateString(),
    )
    .reduce((s, e) => s + (e.end - e.start) / 1000, 0);

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">{title}</h1>
        <span className="text-sm text-[var(--color-text-muted)]">
          {t('today_focus')}{' '}
          <span className="text-[var(--color-text)] font-medium">{fmtDur(todayTotal, locale)}</span>
        </span>
      </div>

      {/* Вкладки.
          Прокручиваются по горизонтали внутри себя: четыре подписи с
          иконками требуют около 480px, и на экране в 360px они тянули
          боковую прокрутку всей страницы — до кнопки «Настройки» было
          не добраться. */}
      <div
        className="flex gap-1 mb-6 border-b border-[var(--color-border)] overflow-x-auto"
        role="tablist"
      >
        {TABS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              onClick={() => {
                setTab(item.id);
              }}
              className={`inline-flex shrink-0 items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                tab === item.id
                  ? 'border-[var(--color-text)] text-[var(--color-text)] font-medium'
                  : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t(`tab_${item.id}`)}
            </button>
          );
        })}
      </div>

      {tab === 'timer' && (
        // Ячейки грида получают min-width: 0 явно. По умолчанию у элемента
        // сетки минимальная ширина равна auto, то есть ширине содержимого:
        // SVG циферблата требовал 325px и распирал колонку вместе со всей
        // страницей на экране в 360px. Пара minmax в шаблоне колонок
        // помогает только на широких экранах, а на телефоне грид
        // одноколоночный.
        <div className="grid lg:grid-cols-[minmax(0,1.5fr)_minmax(300px,0.9fr)] gap-4 items-start [&>*]:min-w-0">
          <FlowTimer flow={flow} />
          <FlowSidebar flow={flow} />
        </div>
      )}
      {tab === 'log' && <FlowLog flow={flow} />}
      {tab === 'reports' && <FlowReports flow={flow} />}
      {tab === 'settings' && <FlowSettingsPanel flow={flow} notify={notify} />}

      <p className="mt-8 text-xs text-[var(--color-text-muted)]">{t('footer_note')}</p>

      {/* Сообщения */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2 pointer-events-none">
        {toasts.map((x) => (
          <div
            key={x.id}
            className={`px-4 py-2.5 rounded-full border text-sm shadow-xl bg-[var(--color-bg-card)] ${
              x.tone === 'ok'
                ? 'border-[var(--color-success-500)]'
                : x.tone === 'error'
                  ? 'border-[var(--color-danger-500)]'
                  : 'border-[var(--color-border)]'
            }`}
          >
            {x.message}
          </div>
        ))}
      </div>
    </div>
  );
}
